import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import ExportButtons from '../components/ExportButtons';

const ONLINE_MIN = 15;   // active within this many minutes = "online"
const ago = ts => {
  if (!ts) return 'never';
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
};
const roleLabel = r => ({ admin:'Master Admin', it_head:'IT Head', it_lead:'IT Lead', it_staff:'Campus IT', requester:'Teaching Staff',
  principal:'HOS', management:'Management', coordinator:'Coordinator', finance:'Finance' }[r] || r);

export default function UserActivity() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => {
    setLoading(true);
    api.get('/logs/activity').then(r => setRows(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  // auto-refresh every 30s so "live now" stays current
  useEffect(() => { const t = setInterval(() => { setNow(Date.now()); load(); }, 30000); return () => clearInterval(t); }, [load]);

  if (!isAdmin) return <div style={{ padding:40, color:'var(--text2)', textAlign:'center' }}>Admin access required</div>;

  const isOnline = u => u.last_seen && (Date.now() - new Date(u.last_seen)) < ONLINE_MIN*60000;
  const online = rows.filter(isOnline);
  const exportRows = rows.map(u => ({ ...u, status: isOnline(u)?'Online':'Offline', last_seen_h: ago(u.last_seen),
    campuses_s: (u.campuses||[]).join(', '), modules_s: Object.entries(u.modules||{}).map(([k,v])=>`${k}:${v}`).join(', ') }));

  const Stat = ({ label, value, color }) => (
    <div className="card" style={{ flex:1, minWidth:150, textAlign:'center' }}>
      <div style={{ fontSize:26, fontWeight:700, color:color||'var(--text)' }}>{value}</div>
      <div style={{ fontSize:12, color:'var(--text2)', marginTop:4 }}>{label}</div>
    </div>
  );

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>User Activity</h2>
          <p style={{ fontSize:13, color:'var(--text2)' }}>Who's online, what they used, and last activity. Auto-refreshes every 30s · "online" = active in the last {ONLINE_MIN} min.</p>
        </div>
        <div className="flex-gap" style={{ gap:8 }}>
          <ExportButtons filename="user-activity" title="User Activity" rows={exportRows}
            columns={[{key:'full_name',label:'User'},{key:'role',label:'Role'},{key:'status',label:'Status'},{key:'last_seen_h',label:'Last Seen'},{key:'last_module',label:'Last Module'},{key:'ip',label:'IP'},{key:'actions_today',label:'Actions (24h)'},{key:'campuses_s',label:'Campuses'},{key:'modules_s',label:'Module Usage (7d)'}]} />
          <button className="btn btn-secondary btn-sm" onClick={load}>↻ Refresh</button>
        </div>
      </div>

      <div className="flex-gap" style={{ gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <Stat label="Online now" value={online.length} color="var(--green)" />
        <Stat label="Active today" value={rows.filter(u => u.actions_today>0).length} color="var(--accent)" />
        <Stat label="Total users" value={rows.length} />
      </div>

      {online.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>🟢 Live now ({online.length})</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {online.map(u => (
              <span key={u.id} style={{ fontSize:12, padding:'5px 12px', borderRadius:16, background:'rgba(52,211,153,0.1)', border:'1px solid rgba(52,211,153,0.4)' }}>
                <span style={{ width:7, height:7, borderRadius:'50%', background:'var(--green)', display:'inline-block', marginRight:6 }} />
                <b>{u.full_name}</b> · {roleLabel(u.role)}{u.last_module?` · ${u.last_module}`:''} · {ago(u.last_seen)}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
        : !rows.length ? <div className="empty-state"><p>No users.</p></div>
        : (
          <div className="table-wrap"><table>
            <thead><tr><th>User</th><th>Role</th><th>Status</th><th>Last Seen</th><th>Last Module</th><th>IP</th><th>Actions (24h)</th><th>Module Usage (7d)</th></tr></thead>
            <tbody>
              {rows.map(u => {
                const on = isOnline(u);
                return (
                  <tr key={u.id}>
                    <td><div style={{ fontWeight:500 }}>{u.full_name}</div><div style={{ fontSize:11, color:'var(--text3)' }}>{u.username}</div></td>
                    <td style={{ fontSize:12 }}>{roleLabel(u.role)}</td>
                    <td><span style={{ fontSize:12, color: on?'var(--green)':'var(--text3)' }}>● {on?'Online':'Offline'}</span></td>
                    <td style={{ fontSize:12, color:'var(--text2)' }}>{ago(u.last_seen)}</td>
                    <td style={{ fontSize:12 }}>{u.last_module || '—'}</td>
                    <td style={{ fontSize:11, color:'var(--text3)' }}>{u.ip || '—'}</td>
                    <td style={{ fontSize:13, textAlign:'center', color: u.actions_today>0?'var(--accent)':'var(--text3)' }}>{u.actions_today}</td>
                    <td>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                        {Object.entries(u.modules||{}).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([m,n]) => (
                          <span key={m} className="badge badge-discuss" style={{ fontSize:10 }}>{m} {n}</span>
                        ))}
                        {!Object.keys(u.modules||{}).length && <span style={{ fontSize:12, color:'var(--text3)' }}>—</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
